import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { action, comparer, computed, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { PopperLayer } from "react-atmosphere";
import { uiStateMobx, userAtom } from "../SharedIframeState";
import {
  ActionType,
  CanvasLayoutPaneRenderType,
  Pane,
  PaneType,
  PaneTypeNames,
  PaneViewMode,
  Position,
} from "../Types";
import { AnimatedPopout } from "../ui/AnimatedPopout";
import { ContextMenu, ContextMenuItem } from "../ui/ContextMenu";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "../ui/Menu";
import { arePanesEqualIgnoringLayout, getParentByFilter } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  canvasLayoutIdAtom,
  focusedPaneIdAtom,
  isEditingLayoutAtom,
  layoutComputed,
  layoutPreviewAddPaneIdBox,
  selectedPaneIdsAtom,
  sidebarHoverPaneIdAtom,
} from "./CanvasState";
import { EvalPaneTemplates } from "./EvalPaneTemplates";
import { FocusPaneEmitter } from "./FocusPane";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import {
  EDIT_LAYOUT_CLASSNAME,
  getAddLayoutPaneRect,
  hasPaneInputMatchingPredicate,
  OVERLAY_PANE_HEADER_BUTTON_CLASSNAME,
  OVERLAY_PANE_HEADER_CLASSNAME,
} from "./SandboxUtils";

function PaneSettingsMenu() {
  return (
    <ContextMenu>
      <ContextMenuItem onClick={() => {}}>hello</ContextMenuItem>
    </ContextMenu>
  );
}

function PaneNameInput({ pane, onClose }: { pane: Pane; onClose: () => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current!.focus();
  }, []);
  const [value, setValue] = useState(pane.name ?? "");
  return (
    <div className="absolute left-[21px] top-1 right-2">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          switch (e.key) {
            case "Escape": {
              onClose();
              break;
            }
            case "Enter": {
              inputRef.current!.blur();
              break;
            }
          }
        }}
        onBlur={(e) => {
          let paneName = value.trim() === "" ? undefined : value;
          if (paneName !== pane.name) {
            canvasDispatch([
              ActionType.SetPaneName,
              {
                paneId: pane.id,
                name: paneName,
              },
            ]);
          }
          onClose();
        }}
        className="block w-full px-1 py-1 font-mono-natto"
        ref={inputRef}
      />
    </div>
  );
}

const LayoutPaneSettingsMenu = observer(
  forwardRef(
    (
      { paneId, onClose }: { paneId: string; onClose: () => void },
      ref: ForwardedRef<HTMLDivElement>
    ) => {
      const layoutId = canvasLayoutIdAtom.value;
      const layoutPane = layoutComputed
        .get()
        ?.panes.find((p) => p.id === paneId);
      const pane = canvasAtom.value.panes.find((p) => p.id === paneId)!;
      return (
        <ContextMenu className="min-w-36" ref={ref}>
          {pane.type === PaneType.Evaluate ? (
            <ContextMenuItem
              onClick={() => {
                if (layoutId !== undefined && layoutPane !== undefined) {
                  canvasDispatch([
                    ActionType.SetLayoutPaneRender,
                    {
                      layoutId,
                      paneId,
                      render:
                        layoutPane.render?.[0] !==
                        CanvasLayoutPaneRenderType.RunButton
                          ? [CanvasLayoutPaneRenderType.RunButton]
                          : undefined,
                    },
                  ]);
                }
                onClose();
              }}
            >
              {layoutPane?.render?.[0] !== CanvasLayoutPaneRenderType.RunButton
                ? "render as run button"
                : "render as pane"}
            </ContextMenuItem>
          ) : null}
          <ContextMenuItem
            onClick={() => {
              if (layoutId !== undefined) {
                canvasDispatch([
                  ActionType.RemovePaneFromLayout,
                  { layoutId, paneId },
                ]);
                runInAction(() => {
                  sidebarHoverPaneIdAtom.value = undefined;
                });
              }
              lastModifyLayoutPanesTime = Date.now();
              onClose();
            }}
          >
            remove from layout
          </ContextMenuItem>
        </ContextMenu>
      );
    }
  )
);

function LayoutPaneSettingsButton({
  paneId,
  showSettings,
  setShowSettings,
}: {
  paneId: string;
  showSettings: boolean;
  setShowSettings: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <DropdownMenuRoot
      open={showSettings}
      onOpenChange={setShowSettings}
      modal={true}
    >
      <DropdownMenuTrigger asChild={true}>
        <button
          className={classNames(
            "flex self-center p-0.5 rounded-sm hover:bg-neutral-200 group-hover:text-neutral-500",
            showSettings
              ? "bg-neutral-200 text-neutral-500"
              : "text-neutral-300"
          )}
        >
          <FeatherIcon icon="more-horizontal" size="sm" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content side="bottom" sideOffset={4}>
          <LayoutPaneSettingsMenu
            paneId={paneId}
            onClose={() => {
              setShowSettings(false);
            }}
          />
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuRoot>
  );
}

let lastModifyLayoutPanesTime: number | undefined;
const PaneSidebarItem = observer(
  ({ paneId, pane }: { paneId: string; pane: Pane }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [showPopup, setShowPopup] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const selectedPaneIds = selectedPaneIdsAtom.value;
    const isSelected = selectedPaneIds.includes(pane.id);
    const isFocused = computed(() => focusedPaneIdAtom.value === pane.id).get();
    const isInputPaneFocused = computed(
      () =>
        focusedPaneIdAtom.value !== undefined &&
        hasPaneInputMatchingPredicate(
          pane,
          (paneId) => paneId === focusedPaneIdAtom.value
        )
    ).get();
    const isInputPaneSelected = hasPaneInputMatchingPredicate(pane, (paneId) =>
      selectedPaneIds.includes(paneId)
    );
    const isOutputPaneFocused = computed(() => {
      if (focusedPaneIdAtom.value === undefined) {
        return false;
      }
      const focusedPane = canvasAtom.value.panes.find(
        (p) => p.id === focusedPaneIdAtom.value
      );
      return (
        focusedPane !== undefined &&
        hasPaneInputMatchingPredicate(
          focusedPane,
          (paneId) => paneId === pane.id
        )
      );
    }).get();
    let isOutputPaneSelected = selectedPaneIds.some((selectedPaneId) => {
      const selectedPane = canvasAtom.value.panes.find(
        (p) => p.id === selectedPaneId
      );
      return (
        selectedPane !== undefined &&
        hasPaneInputMatchingPredicate(
          selectedPane,
          (paneId) => paneId === pane.id
        )
      );
    });
    const activePaneState = activePaneStates.get(paneId)!;
    const isHidden = activePaneState.viewMode.get() === PaneViewMode.Hidden;
    const paneName =
      pane.type === PaneType.EnvironmentVariable
        ? pane.name ?? pane.key
        : pane.name;

    let evaluateExpression: string | undefined;
    if (
      pane.type === PaneType.Evaluate ||
      pane.type === PaneType.EvaluateGlobal
    ) {
      evaluateExpression = pane.expression;
    }

    const [showSettings, setShowSettings] = useState(false);
    const isHovered = computed(
      () => showSettings || sidebarHoverPaneIdAtom.value === pane.id
    ).get();

    return (
      <div
        onMouseEnter={action(() => {
          if (
            lastModifyLayoutPanesTime !== undefined &&
            lastModifyLayoutPanesTime > Date.now() - 200
          ) {
            // if the user just added a pane, the sidebar will reshuffle and
            // their mouse may be over another item. skip showing this so they
            // focus on the newly added pane.
            return;
          }
          const layout = layoutComputed.get();
          if (
            layout !== undefined &&
            layout.panes.every((p) => p.id !== paneId)
          ) {
            layoutPreviewAddPaneIdBox.set(paneId);
          }
          sidebarHoverPaneIdAtom.value = pane.id;
        })}
        onMouseLeave={action(() => {
          if (layoutPreviewAddPaneIdBox.get() === paneId) {
            layoutPreviewAddPaneIdBox.set(undefined);
          }
          if (sidebarHoverPaneIdAtom.value === pane.id) {
            sidebarHoverPaneIdAtom.value = undefined;
          }
        })}
        className="relative group"
        ref={rootRef}
      >
        <div
          className={classNames(
            "h-8 border flex pr-2 overflow-hidden",
            !isFocused && isHovered
              ? isHidden
                ? "border-neutral-300"
                : "border-primary-300"
              : "border-transparent",
            isFocused || isSelected ? "bg-primary-100" : undefined
          )}
        >
          <button
            onClick={(e) => {
              if (!isHidden) {
                FocusPaneEmitter.emit("focus", {
                  paneId: pane.id,
                });
              }
            }}
            onDoubleClick={() => {
              setIsEditingName(true);
              setShowPopup(false);
            }}
            className="font-mono-natto grow flex items-center overflow-hidden text-left pl-2 pr-1 cursor-default whitespace-nowrap"
          >
            <FeatherIcon
              icon={
                isFocused || isSelected
                  ? "credit-card"
                  : isInputPaneFocused || isInputPaneSelected
                  ? "arrow-right"
                  : isOutputPaneFocused || isOutputPaneSelected
                  ? "arrow-left"
                  : "credit-card"
              }
              size="xs"
              className={classNames(
                "mr-1.5 shrink-0",
                isFocused ? "text-neutral-500" : "text-neutral-300"
              )}
            />
            {paneName !== undefined ? (
              <div className="overflow-hidden grow text-ellipsis">
                {paneName}
              </div>
            ) : (
              <div className="flex grow items-center overflow-hidden">
                {pane.type === PaneType.Import &&
                pane.module !== undefined &&
                pane.module[0] === "npm" ? (
                  <div className="shrink-0 overflow-hidden text-ellipsis">
                    npm {pane.module[1]}
                  </div>
                ) : pane.type === PaneType.EvaluateTemplate ? (
                  <>
                    <div className="text-neutral-400">eval</div>
                    <div className="text-neutral-300 ml-2 overflow-hidden text-ellipsis">
                      {EvalPaneTemplates[pane.templateId]?.schema.title}
                    </div>
                  </>
                ) : (
                  <div
                    className={classNames(
                      isFocused || isSelected
                        ? "text-neutral-500"
                        : "text-neutral-400"
                    )}
                  >
                    {PaneTypeNames[pane.type]}
                  </div>
                )}
                {evaluateExpression !== undefined ? (
                  <div
                    className={classNames(
                      "ml-2 overflow-hidden text-ellipsis",
                      isFocused || isSelected
                        ? "text-neutral-400"
                        : "text-neutral-300"
                    )}
                  >
                    {evaluateExpression}
                  </div>
                ) : null}
              </div>
            )}
          </button>
          {isHidden ? (
            <button
              onClick={action(() => {
                lastModifyLayoutPanesTime = Date.now();
                const layoutZ = layoutComputed.get()?.panes.map((p) => p.z);
                const maxLayoutZ =
                  layoutZ !== undefined && layoutZ.length > 0
                    ? Math.max(...layoutZ)
                    : 0;
                const pane = canvasAtom.value.panes.find(
                  (p) => p.id === paneId
                )!;
                canvasDispatch([
                  ActionType.AddPaneToLayout,
                  {
                    layoutId: canvasLayoutIdAtom.value!,
                    paneId,
                    rect: getAddLayoutPaneRect(paneId),
                    z: maxLayoutZ + 1,
                    ...(pane.type === PaneType.Evaluate ||
                    pane.type === PaneType.State ||
                    pane.type === PaneType.EvaluateTemplate
                      ? { editorHeight: 0 }
                      : {}),
                  },
                ]);
                FocusPaneEmitter.emit("focus", {
                  paneId: pane.id,
                });
                layoutPreviewAddPaneIdBox.set(undefined);
                sidebarHoverPaneIdAtom.value = undefined;
              })}
              className="flex self-center shrink-0 px-1.5 py-0.5 rounded-sm button-color transition text-2xs font-mono-natto uppercase"
            >
              add
            </button>
          ) : (
            <LayoutPaneSettingsButton
              paneId={paneId}
              showSettings={showSettings}
              setShowSettings={setShowSettings}
            />
          )}
          {/* <button
        onClick={() => setShowPopup(true)}
        className={classNames(
          "flex transition-all absolute right-2 top-2",
          showPopup || isFocused
            ? "text-neutral-700 opacity-100"
            : "text-neutral-300 group-hover:text-neutral-700 opacity-50 hover:opacity-100"
        )}
      >
        <FeatherIcon icon="ellipsis" />
      </button> */}
          {isEditingName ? (
            <PaneNameInput
              pane={pane}
              onClose={() => setIsEditingName(false)}
            />
          ) : null}
          {showPopup ? (
            <PopperLayer
              reference={rootRef}
              render={({ state, completeTransitionExit }) => (
                <AnimatedPopout
                  state={state}
                  completeTransitionExit={completeTransitionExit}
                  transformOrigin="top left"
                >
                  <PaneSettingsMenu />
                </AnimatedPopout>
              )}
              options={{
                placement: "right",
                modifiers: [{ name: "offset", options: { offset: [0, 4] } }],
              }}
              onOutsideMouseDown={() => setShowPopup(false)}
              transitionExit={true}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

const PaneSidebarItemObserver = observer(({ paneId }: { paneId: string }) => {
  const pane = computed(
    () => canvasAtom.value.panes.find((p) => p.id === paneId),
    { equals: arePanesEqualIgnoringLayout }
  ).get();
  if (pane === undefined) {
    return null;
  }
  return <PaneSidebarItem paneId={paneId} pane={pane} />;
});

const SessionUnit = observer(() => {
  const user = userAtom.value;
  if (user === undefined) {
    return (
      <button
        onClick={() => {
          ParentComlinkAPI.showLoginDialog("sidebar bottom");
        }}
        className="w-full flex items-center h-[36px] pl-4 font-medium text-neutral-400 hover:text-neutral-700 hover:bg-neutral-125 transition-colors"
      >
        <FeatherIcon icon="user" size="md" className="opacity-50 mr-1.5" />
        log in
      </button>
    );
  }
  return (
    <div className="flex items-center font-medium text-neutral-400 pl-4 h-[36px]">
      <FeatherIcon icon="user" size="md" className="opacity-50 mr-1.5" />
      hi {user.username ?? "anonymous"}!
    </div>
  );
});

type SidebarPaneGroup = { label?: string; paneIds: string[] };
type SidebarViewType = "default" | "valueType";

function SidebarViewSelector({
  viewType,
  setViewType,
}: {
  viewType: SidebarViewType;
  setViewType: React.Dispatch<React.SetStateAction<SidebarViewType>>;
}) {
  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger className="p-1 text-neutral-300 hover:text-neutral-500 radix-open:text-neutral-500 transition">
        <FeatherIcon icon="chevron-down" size="sm" />
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={4}>
        <DropdownMenuItem
          onClick={() => {
            setViewType("default");
          }}
          className="justify-between"
        >
          default
          {viewType === "default" ? (
            <FeatherIcon icon="check" size="sm" />
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setViewType("valueType");
          }}
          className="justify-between"
        >
          show errors first
          {viewType === "valueType" ? (
            <FeatherIcon icon="check" size="sm" />
          ) : null}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}

function computeSidebarGroups(
  panes: Pane[],
  viewType: SidebarViewType
): SidebarPaneGroup[] {
  const paneIds = [...activePaneStates.keys()];
  switch (viewType) {
    case "default": {
      return paneIds.length > 0 ? [{ paneIds }] : [];
    }
    case "valueType": {
      const errorPaneIds = paneIds.filter(
        (paneId) =>
          activePaneStates.get(paneId)!.outputBoxes[0].get()[0] === "error"
      );
      const waitingPaneIds = paneIds.filter(
        (paneId) =>
          activePaneStates.get(paneId)!.outputBoxes[0].get()[0] === "waiting"
      );
      const valuePaneIds = paneIds.filter((paneId) =>
        ["running", "value"].includes(
          activePaneStates.get(paneId)!.outputBoxes[0].get()[0]
        )
      );
      return [
        errorPaneIds.length > 0
          ? [{ label: "error", paneIds: errorPaneIds }]
          : [],
        waitingPaneIds.length > 0
          ? [{ label: "waiting", paneIds: waitingPaneIds }]
          : [],
        valuePaneIds.length > 0
          ? [{ label: "value", paneIds: valuePaneIds }]
          : [],
      ].flat();
    }
  }
}

export const LayoutEditPane = observer(() => {
  const allPaneIds = computed(() => canvasAtom.value.panes.map((p) => p.id), {
    equals: comparer.structural,
  }).get();
  const layoutVisiblePaneIds = computed(
    () => layoutComputed.get()?.panes.map((p) => p.id),
    { equals: comparer.structural }
  ).get();
  const paneGroups = [
    { label: "in layout", paneIds: layoutVisiblePaneIds ?? allPaneIds },
    {
      label: "not in layout",
      paneIds:
        layoutVisiblePaneIds !== undefined
          ? allPaneIds.filter(
              (paneId) => !layoutVisiblePaneIds.includes(paneId)
            )
          : [],
    },
  ].filter((group) => group.paneIds.length > 0);
  // const paneGroups = computed(
  //   () => computeSidebarGroups(canvasAtom.value.panes, viewType),
  //   {
  //     equals: comparer.structural,
  //   }
  // ).get();

  const rootRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef<Position>([0, 0]);
  const bindDrag = useDrag(
    action<Handler<"drag">>(({ target, first, last, offset, cancel }) => {
      if (
        first &&
        target instanceof Element &&
        getParentByFilter(target, (el) => el.tagName === "BUTTON") !== undefined
      ) {
        cancel();
        return;
      }
      rootRef.current!.style.transform = `translate(${offset[0]}px, ${offset[1]}px)`;
      offsetRef.current = offset;
      if (first && target instanceof HTMLElement) {
        target.style.cursor = "grabbing";
      }
      if (last && target instanceof HTMLElement) {
        target.style.cursor = "";
      }
    }),
    { from: () => offsetRef.current }
  );

  return (
    <div
      className={classNames(
        "bg-neutral-50 w-[12.5rem] h-[50vh] fixed right-4 top-20 md:top-16 flex flex-col rounded overflow-hidden shadow-xl",
        EDIT_LAYOUT_CLASSNAME,
        {
          hidden: computed(
            () => uiStateMobx.maximizedPaneId !== undefined
          ).get(),
        }
      )}
      ref={rootRef}
    >
      <div
        className={classNames(
          OVERLAY_PANE_HEADER_CLASSNAME,
          "h-8 px-2 flex items-center cursor-grab group shrink-0 touch-none"
        )}
        {...bindDrag()}
      >
        <button
          onClick={action(() => {
            isEditingLayoutAtom.value = false;
          })}
          className={classNames(OVERLAY_PANE_HEADER_BUTTON_CLASSNAME, "mr-2")}
        >
          <FeatherIcon icon="x" size="xs" />
        </button>
        <div>edit layout</div>
      </div>
      <div className="overflow-auto grow pb-4">
        <div className="border-b bg-secondary-50 border-secondary-200 p-2 text-neutral-500">
          Add and hide panes in edit layout mode. To finish editing, close this
          pane.
        </div>
        <div className="relative">
          {paneGroups.map(({ label, paneIds }, i) => (
            <div className="border-t border-neutral-150 first:border-0" key={i}>
              {label !== undefined ? (
                <div className="border-b border-neutral-150 px-2 py-1 text-2xs uppercase font-mono-natto text-primary-400">
                  <div className="relative top-px">{label}</div>
                </div>
              ) : null}
              {paneIds.map((paneId) => (
                <PaneSidebarItemObserver paneId={paneId} key={paneId} />
              ))}
            </div>
          ))}
          {allPaneIds.length === 0 ? (
            <div className="pl-2 pt-2 text-neutral-400">
              <button
                onClick={() => {
                  ParentComlinkAPI.switchLayoutId(undefined);
                }}
                className="text-primary-400 hover:text-primary-500 transition"
              >
                switch to source
              </button>{" "}
              to create panes
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});
